import {
  Radiomark,
  Table,
  TableCellProps,
  TableColumnHeaderProps,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { MBox, MText } from '../../../components/Monetize';
import { BlankText } from '../../../components/Monetize/BlankText';
import { itemControlBase } from '../../../styles/chakra/components/RadioGroup';
import { AccountToKeep } from './AccountMergeTable.types';

/*
 * v3 port: the two call sites of this component live in separate table
 * cells (`AccountMergeComparisonTable.tsx`'s header row), so there's no
 * single element to root a `RadioGroup.Root` on without breaking the
 * table's row/cell structure — `RadioGroup.Item` needs that ancestor for
 * context and isn't an option here. Hand-rolled instead: a real native
 * `<input type="radio">` (kept for the same-`name` native grouping and
 * keyboard behavior the v2 `Radio` also relied on under the hood) styled
 * with `VisuallyHidden asChild`, paired with `Radiomark` — the same visual
 * mark `RadioGroup.Item` renders internally — for the dot, using
 * `itemControlBase` so it matches the design exactly rather than
 * `Radiomark`'s own unrelated default recipe.
 */
export function KeepAccountRadioOption({
  value,
  selectedValue,
  onChecked,
}: {
  value: AccountToKeep;
  selectedValue: AccountToKeep;
  onChecked: () => void;
}) {
  const checked = selectedValue === value;
  return (
    <MBox as="label" display="inline-flex" alignItems="center" cursor="pointer">
      <VisuallyHidden asChild>
        <input
          type="radio"
          name="accountToKeep"
          value={value}
          checked={checked}
          onChange={() => onChecked()}
        />
      </VisuallyHidden>
      <Radiomark
        checked={checked}
        css={{ ...itemControlBase, boxSize: 4 }}
        mr={2}
      />
      <MBox as="span" fontWeight={checked ? '600' : 'normal'}>
        Keep This Account
      </MBox>{' '}
      {!checked && (
        <MBox as="span" fontWeight="600" color="tRed.base">
          (This account will be archived)
        </MBox>
      )}
    </MBox>
  );
}

export const ThData = ({
  children,
  ...props
}: { children?: string } & TableColumnHeaderProps) => {
  return (
    <Table.ColumnHeader
      textTransform="none"
      letterSpacing="inherit"
      textAlign="right"
      width="200px"
      borderRight="1px solid {colors.tGray.back}"
      {...props}
    >
      <MText>{children}</MText>
    </Table.ColumnHeader>
  );
};

export const TrData = ({
  children,
  omitWrap,
  cellStyle,
  blankRenderer = () => <BlankText />,
  ...props
}: {
  omitWrap?: boolean;
  cellStyle?: 'KEEP' | 'DELETE' | 'NONE';
  blankRenderer?: () => ReactNode;
  children?: ReactNode;
} & TableCellProps) => {
  function getBackgroundColor() {
    switch (cellStyle) {
      case 'KEEP':
        return 'tGreen.light';
      case 'DELETE':
        return 'tRed.cosmos';
      default:
        return undefined;
    }
  }

  if (omitWrap) {
    return (
      <Table.Cell width="30vw" maxW="30vw" bg={getBackgroundColor()} {...props}>
        {children}
      </Table.Cell>
    );
  }

  return (
    <Table.Cell width="30vw" maxW="30vw" bg={getBackgroundColor()} {...props}>
      {children ? (
        <MText lineClamp={3} whiteSpace="break-spaces" wordBreak="break-word">
          {children}
        </MText>
      ) : (
        blankRenderer()
      )}
    </Table.Cell>
  );
};
